/**
 * Materialize consolidated-map items into the site's own pages.
 *
 * The engine's map (sidecar store) and the site's hierarchy (saas-core)
 * are separate planes: saas persists only what the user accepts. This is
 * that accept step — invoked from the chat's Life Map panel, it creates
 * real dreams/goals/skills/habits/tasks through the existing create APIs,
 * carrying each item's kind, importance and schedule:
 *
 *   engine kind          site type   carried over
 *   dream, direction  →  Dream       title (their words), area → category
 *   goal              →  Goal        + parent dream via `serves` edges
 *   skill_gap         →  Skill       + life_area
 *   habit             →  Habit       + cadence, RRULE, days, time window
 *   habit_candidate   →  Habit when recurrence was stated, else Task — the
 *                        engine's G3 rule re-types candidates the moment a
 *                        cadence/schedule/deadline is stated, so a candidate
 *                        still on the map has NO stated recurrence and a page
 *                        Habit would fabricate one.
 *   task              →  Task        + deadline → due date, `at` → due time
 *                        (2026-09-12: "buy eggs at 5pm" landed as the day
 *                        with no hour — the accept step carried `at` for
 *                        habits only) + the engine id, so a later turn that
 *                        refines the item can find its page copy
 *   value / identity_theme / person_perception stay engine-only: they are
 *   self-knowledge, not to-dos, and have no page to live on.
 *
 * The user has the last word on all of it: per-item overrides (title, target
 * type, chosen parent, excluded) and user-authored custom drafts are stored
 * per user and applied here at creation time. The engine map is never
 * written — saas persists only what the user accepted, as edited.
 *
 * Importance: the engine tracks confidence, the site tracks priority —
 * ≥0.8 → high, ≥0.5 → medium, else low (overridable on the item page).
 *
 * Parent chains: adding a goal whose dream isn't on the pages yet creates
 * the dream first, so nothing lands as an orphan.
 *
 * Dedup: every created item is recorded in a per-user ledger
 * (localStorage `yanamas-materialized:<uid>`) keyed by ENGINE id, so the
 * same item can't be added twice from this device and re-syncs are stable.
 */
import { DreamService } from './dreamService'
import { GoalService } from './goalService'
import { TaskService } from './taskService'
import { HabitService, type Weekday, type TimeWindow } from './habitService'
import { SkillService } from './skillService'
import { placeService, PLACE_AUTO_ATTACH_THRESHOLD } from './placeService'
import type { LifeMapItem } from './hortonV3Service'
import { BRAND } from '../config/brand'

export type MaterializedType = 'dream' | 'goal' | 'skill' | 'habit' | 'task'

const KIND_TO_TYPE: Record<string, MaterializedType> = {
  dream: 'dream',
  direction: 'dream',
  goal: 'goal',
  skill_gap: 'skill',
  skill: 'skill',      // user-authored drafts carry the page type as their kind
  habit: 'habit',
  task: 'task',
}

/** The same recurrence predicate the engine's G3/item_kind rule reads: a
 * compiled RRULE, stated days or per-day slots, or a recurring cadence word.
 * "once" and "unstated" are NOT recurrence — law 5, absence is not a fact. */
export function statesRecurrence(item: LifeMapItem): boolean {
  if (item.schedule?.rrules?.length) return true
  const T: Record<string, unknown> = item.temporal || {}
  if (Array.isArray(T.days) && T.days.length) return true
  if (Array.isArray(T.slots) && T.slots.length) return true
  const rec = String(T.recurrence || item.cadence || '').toLowerCase()
  return Boolean(rec) && rec !== 'once' && rec !== 'unstated'
}

export function materializableType(item: LifeMapItem): MaterializedType | null {
  // habit_candidate = the engine could NOT conclude habit-vs-task (a stated
  // recurrence or deadline would already have re-typed it). Default the page
  // type from the one fact the row may still carry; the user can override.
  if (item.kind === 'habit_candidate') return statesRecurrence(item) ? 'habit' : 'task'
  return KIND_TO_TYPE[item.kind] ?? null
}

function priorityFromConfidence(c: number | undefined): 'low' | 'medium' | 'high' {
  if ((c ?? 0) >= 0.8) return 'high'
  if ((c ?? 0) >= 0.5) return 'medium'
  return 'low'
}

function areaOf(item: LifeMapItem): string | undefined {
  return (item.life_areas && item.life_areas[0]) || item.life_area || undefined
}

function descriptionOf(item: LifeMapItem): string | undefined {
  return item.summary || item.rationale || undefined
}

const CADENCES = ['daily', 'weekly', 'biweekly', 'monthly'] as const
function cadenceOf(item: LifeMapItem): string | undefined {
  const raw = (item.cadence || (item.temporal as any)?.recurrence || '').toLowerCase()
  if (!raw) return undefined
  if (raw.includes('biweek') || raw.includes('every other week')) return 'biweekly'
  if (raw.includes('day')) return 'daily'
  if (raw.includes('week')) return 'weekly'
  if (raw.includes('month')) return 'monthly'
  return CADENCES.includes(raw as any) ? raw : undefined
}

const DAY_MAP: Record<string, Weekday> = {
  mon: 'monday', tue: 'tuesday', wed: 'wednesday', thu: 'thursday',
  fri: 'friday', sat: 'saturday', sun: 'sunday',
}
function daysOf(item: LifeMapItem): Weekday[] | undefined {
  const T = (item.temporal || {}) as Record<string, any>
  const days: string[] = Array.isArray(T.days) ? T.days
    : Array.isArray(T.slots) ? T.slots.map((s: any) => s?.day).filter(Boolean) : []
  const mapped = days.map((d) => DAY_MAP[String(d).slice(0, 3).toLowerCase()]).filter(Boolean) as Weekday[]
  return mapped.length ? [...new Set(mapped)] : undefined
}

const WINDOWS: TimeWindow[] = ['morning', 'afternoon', 'evening', 'night']
function windowOf(item: LifeMapItem): TimeWindow | undefined {
  const w = String((item.temporal as any)?.part_of_day || '').toLowerCase()
  return WINDOWS.includes(w as TimeWindow) ? (w as TimeWindow) : undefined
}

function dueDateOf(item: LifeMapItem): string | undefined {
  const d = (item.temporal as any)?.deadline
  return typeof d === 'string' && d ? d : undefined
}

// ── Ledger: engine id → created saas item ─────────────────────────────────

export interface LedgerEntry {
  type: MaterializedType
  saasId: number
  title: string
  /** What the engine row said when the page copy was last written — title,
   *  schedule, summary, place. When the map later says something different
   *  (a refinement said in chat after the save: "6" → 18:00), the panel
   *  pushes the page copy to match. Absent on entries from before 2026-09-12;
   *  those follow on their first observed change. */
  fp?: string
}
export type Ledger = Record<string, LedgerEntry>

/** The facts a page copy is made from, as one comparable string. Overrides
 *  are deliberately NOT part of it: the user's own edits already sync on
 *  edit; this tracks what the ENGINE learned. */
export function fingerprintOf(item: LifeMapItem): string {
  const T = (item.temporal || {}) as Record<string, unknown>
  const keys = Object.keys(T).sort()
  const temporal = keys.map((k) => `${k}=${JSON.stringify(T[k])}`).join(',')
  const turns = [...(item.turn_ids ?? [])].sort((a, b) => a - b).join(',')
  return [item.kind, item.title, item.display_title ?? '', item.summary ?? '',
          item.location ?? '', item.schedule?.rrule ?? '', temporal, turns].join('|')
}

/** Ledger entries whose engine row has changed since the page copy was
 *  written — what `followMap` should push. Entries without a fingerprint
 *  (older) count as unchanged and get stamped on their next sync. */
export function staleCommitted(items: Iterable<LifeMapItem>, ledger: Ledger): LifeMapItem[] {
  const out: LifeMapItem[] = []
  for (const it of items) {
    const e = ledger[String(it.id)]
    if (!e || e.fp === undefined) continue
    if (e.fp !== fingerprintOf(it)) out.push(it)
  }
  return out
}

const ledgerKey = (uid: string) => `${BRAND.assistant}-materialized:${uid}`

export function loadLedger(uid: string): Ledger {
  try { return JSON.parse(localStorage.getItem(ledgerKey(uid)) || '{}') } catch { return {} }
}

function saveLedger(uid: string, ledger: Ledger) {
  try { localStorage.setItem(ledgerKey(uid), JSON.stringify(ledger)) } catch { /* full */ }
}

// ── User edits on the proposal ────────────────────────────────────────────
//
// The engine map is a PROPOSAL. Before accepting, the user may retitle an
// item, change its page type, place it under a different parent (drag &
// drop), or leave it off entirely. These are theirs, kept per user on the
// device and applied at creation time — the engine row is never touched.

export interface ItemOverride {
  title?: string
  type?: MaterializedType
  excluded?: boolean
  /** Engine id of the chosen parent; null = top level; absent = engine's edge. */
  parent?: number | null
  // Field edits, one per creation field. `undefined` = no override (engine
  // default applies); an empty string / empty list / 0 = "cleared by the
  // user" and materializes as absent.
  description?: string
  priority?: 'low' | 'medium' | 'high'
  area?: string
  cadence?: string
  days?: Weekday[]
  window?: TimeWindow | ''
  at?: string
  duration?: number
  due?: string
}
export type Overrides = Record<string, ItemOverride>

/** The creation fields as `createOne` would fill them from the engine row. */
export interface ItemFields {
  description?: string
  priority: 'low' | 'medium' | 'high'
  area?: string
  cadence?: string
  days?: Weekday[]
  window?: TimeWindow
  /** Start time "HH:MM" — what makes the schedule gate expand occurrences.
   * A habit created without it never appears on Today and Auto-Mom never
   * sees it (measured 2026-08-23: expand=SKIP on every chat-created habit). */
  at?: string
  duration?: number
  due?: string
}

function startTimeOf(item: LifeMapItem): string | undefined {
  const T: Record<string, unknown> = item.temporal || {}
  if (typeof T.at === 'string' && T.at) return T.at
  const slots = Array.isArray(T.slots) ? (T.slots as Array<{ at?: string | null }>) : []
  const first = slots.map((s) => s?.at).find((a) => typeof a === 'string' && a)
  return (first as string | undefined) || undefined
}

export function defaultFieldsOf(item: LifeMapItem): ItemFields {
  return {
    description: descriptionOf(item),
    priority: priorityFromConfidence(item.confidence),
    area: areaOf(item),
    cadence: cadenceOf(item),
    days: daysOf(item),
    window: windowOf(item),
    at: startTimeOf(item),
    duration: (((item.temporal || {}) as Record<string, unknown>).duration_minutes as number | undefined) || undefined,
    due: dueDateOf(item),
  }
}

/** Engine defaults with the user's field edits applied; cleared values
 * ('' / [] / 0) come out as absent. */
export function effFields(item: LifeMapItem, ov?: ItemOverride): ItemFields {
  const d = defaultFieldsOf(item)
  return {
    description: ov?.description !== undefined ? (ov.description || undefined) : d.description,
    priority: ov?.priority ?? d.priority,
    area: ov?.area !== undefined ? (ov.area || undefined) : d.area,
    cadence: ov?.cadence !== undefined ? (ov.cadence || undefined) : d.cadence,
    days: ov?.days !== undefined ? (ov.days.length ? ov.days : undefined) : d.days,
    window: ov?.window !== undefined ? (ov.window || undefined) : d.window,
    at: ov?.at !== undefined ? (ov.at || undefined) : d.at,
    duration: ov?.duration !== undefined ? (ov.duration || undefined) : d.duration,
    due: ov?.due !== undefined ? (ov.due || undefined) : d.due,
  }
}

/** Did the user touch the schedule? Then the engine's compiled RRULE no
 * longer describes this habit and must not be sent alongside their edit. */
function scheduleEdited(ov?: ItemOverride): boolean {
  return Boolean(ov && (ov.cadence !== undefined || ov.days !== undefined
    || ov.window !== undefined || ov.at !== undefined || ov.duration !== undefined))
}

const overridesKey = (uid: string) => `${BRAND.assistant}-lifemap-overrides:${uid}`

export function loadOverrides(uid: string): Overrides {
  try { return JSON.parse(localStorage.getItem(overridesKey(uid)) || '{}') } catch { return {} }
}

export function saveOverrides(uid: string, o: Overrides) {
  try { localStorage.setItem(overridesKey(uid), JSON.stringify(o)) } catch { /* full */ }
}

/** A user-authored addition to the proposal. Negative id so it can never
 * collide with an engine item; it rides the same ledger once created. */
export interface CustomDraft {
  id: number
  title: string
  type: MaterializedType
  parent: number | null
}

const customKey = (uid: string) => `${BRAND.assistant}-lifemap-custom:${uid}`

export function loadCustom(uid: string): CustomDraft[] {
  try {
    const v = JSON.parse(localStorage.getItem(customKey(uid)) || '[]')
    return Array.isArray(v) ? v : []
  } catch { return [] }
}

export function saveCustom(uid: string, drafts: CustomDraft[]) {
  try { localStorage.setItem(customKey(uid), JSON.stringify(drafts)) } catch { /* full */ }
}

/** Present a draft as a map item so one render/create path serves both. */
export function customToItem(d: CustomDraft): LifeMapItem {
  return {
    id: d.id, kind: d.type, title: d.title, status: 'yours',
    confidence: 1, life_area: null,
  }
}

// ── Creation ──────────────────────────────────────────────────────────────

const dreams = new DreamService()
const goals = new GoalService()
const tasks = new TaskService()
const habits = new HabitService()
const skills = new SkillService()

interface ParentIds { parent_dream_id?: number; parent_goal_id?: number }

/**
 * A task's place, resolved exactly the way the edit form's picker does it:
 * the backend ranks the user's saved places against the task's text, and
 * only a confident match attaches ("buy groceries in Millbrae" → the saved
 * Trader Joe's). The engine's stated `location` rides along as text either
 * way, so the edit page can still suggest later. Best-effort: a suggest
 * failure never blocks the create.
 */
async function resolvePlaceId(item: LifeMapItem, title: string, description?: string): Promise<number | null> {
  const text = [title, item.location ?? '', description ?? ''].join(' ').trim()
  if (text.length < 3) return null
  try {
    const candidates = await placeService.suggestForTask(text, 1)
    const top = candidates[0]
    return top && top.confidence >= PLACE_AUTO_ATTACH_THRESHOLD ? top.place_id : null
  } catch {
    return null
  }
}

async function createOne(item: LifeMapItem, type: MaterializedType, parents: ParentIds, title: string, ov?: ItemOverride): Promise<number> {
  const F = effFields(item, ov)
  const { description, priority, area } = F
  switch (type) {
    case 'dream': {
      const d = await dreams.createDream({ title, description, priority, category: area, status: 'active' })
      return d.id
    }
    case 'goal': {
      const g = await goals.createGoal({
        title, description, priority, category: area,
        parent_dream_id: parents.parent_dream_id,
        status: 'active',
      })
      return g.id
    }
    case 'skill': {
      const s = await skills.createSkill({
        title, description, priority, life_area: area,
        parent_goal_id: parents.parent_goal_id, parent_dream_id: parents.parent_dream_id,
      })
      return Number(s.id)
    }
    case 'habit': {
      const h = await habits.createHabit({
        title, description, priority, life_area: area,
        target_cadence: F.cadence,
        preferred_days: F.days,
        preferred_time_window: F.window,
        // The schedule gate expands Today-occurrences ONLY when a concrete
        // time exists — without it the habit never reaches Today/Auto-Mom.
        preferred_start_time: F.at,
        duration_minutes: F.duration,
        rrule: scheduleEdited(ov) ? undefined : item.schedule?.rrule || undefined,
        parent_goal_id: parents.parent_goal_id, parent_dream_id: parents.parent_dream_id,
      })
      return Number(h.id)
    }
    case 'task': {
      const placeId = await resolvePlaceId(item, title, description)
      const t = await tasks.createTask({
        title, description, category: area,
        priority,
        due_date: F.due,
        // The stated hour of a one-off is its complete-by clock; the server
        // snapshots the zone. Without it "remind me at 5pm" had no 5pm.
        due_time: F.at,
        // Custom drafts carry a negative id and have no engine row to link.
        lifecoach_hypothesis_id: item.id > 0 ? item.id : undefined,
        lifecoach_turn_ids: item.turn_ids?.length ? item.turn_ids : undefined,
        location: item.location || undefined,
        place_id: placeId ?? undefined,
        parent_goal_id: parents.parent_goal_id, parent_dream_id: parents.parent_dream_id,
      })
      return t.id
    }
  }
}

export interface MaterializeDeps {
  uid: string
  /** Parent lookup over the tree AS DISPLAYED — engine edges with the
   * user's drag-and-drop placements already applied. */
  parentOf: (id: number) => LifeMapItem | null
  /** Children as displayed — needed to re-point committed children when a
   * committed parent is recreated under a new type. */
  childrenOf?: (id: number) => LifeMapItem[]
  /** Effective page type: the user's override, else the kind default.
   * Return null for excluded items — the chain walks past them. */
  typeOf?: (item: LifeMapItem) => MaterializedType | null
  /** Effective title: the user's retitle, else their own words. */
  titleOf?: (item: LifeMapItem) => string
  /** The user's stored edits for an item — field values, if any. */
  overrideOf?: (item: LifeMapItem) => ItemOverride | undefined
}

/**
 * Create this item on the site pages, creating any mappable ancestors
 * first so parent links hold. Returns every ledger entry it created
 * (ancestors included). Items already in the ledger are not re-created.
 */
export async function materialize(item: LifeMapItem, deps: MaterializeDeps): Promise<Ledger> {
  const ledger = loadLedger(deps.uid)
  const created: Ledger = {}
  const typeOf = deps.typeOf ?? materializableType
  const titleOf = deps.titleOf ?? ((i: LifeMapItem) => i.display_title || i.title)

  // Ancestor chain, top-down (nearest-root first), mappable kinds only.
  // An excluded ancestor is skipped, not a wall: its child attaches to the
  // nearest kept ancestor above it.
  const chain: LifeMapItem[] = []
  let cur: LifeMapItem | null = item
  const guard = new Set<number>()
  while (cur && !guard.has(cur.id)) {
    guard.add(cur.id)
    if (typeOf(cur)) chain.unshift(cur)
    cur = deps.parentOf(cur.id)
  }
  if (chain.length === 0) throw new Error(`"${item.title}" is self-knowledge — it stays on the map`)

  // Walk down, threading created parent ids into children.
  const parents: ParentIds = {}
  for (const node of chain) {
    const type = typeOf(node)!
    const key = String(node.id)
    let entry = ledger[key]
    if (!entry) {
      const saasId = await createOne(node, type, parents, titleOf(node), deps.overrideOf?.(node))
      entry = { type, saasId, title: titleOf(node), fp: fingerprintOf(node) }
      ledger[key] = entry
      created[key] = entry
      saveLedger(deps.uid, ledger)
    }
    if (entry.type === 'dream') { parents.parent_dream_id = entry.saasId; parents.parent_goal_id = undefined }
    if (entry.type === 'goal') parents.parent_goal_id = entry.saasId
  }
  return created
}

// ── Editing what is ALREADY on the pages ──────────────────────────────────
//
// Acceptance is not a one-way door: an item the user put on their pages can
// still be retitled, refielded, re-parented and retyped from the map, and
// the page item follows. Field edits go through the type's update API;
// a TYPE change recreates the page item (delete + create) because nothing
// converts a Task row into a Habit row in place — committed children are
// re-pointed at the new id afterwards.

/** Nearest committed goal/dream ancestors of `item` in the displayed tree —
 * what the page item's parent pointers should say. */
function committedParentIds(item: LifeMapItem, deps: MaterializeDeps, ledger: Ledger): ParentIds {
  const out: ParentIds = {}
  const guard = new Set<number>([item.id])
  let cur = deps.parentOf(item.id)
  while (cur && !guard.has(cur.id)) {
    guard.add(cur.id)
    const e = ledger[String(cur.id)]
    if (e) {
      if (e.type === 'goal' && out.parent_goal_id == null) out.parent_goal_id = e.saasId
      if (e.type === 'dream') { out.parent_dream_id = e.saasId; break }
    }
    cur = deps.parentOf(cur.id)
  }
  return out
}

async function updateOne(entry: LedgerEntry, item: LifeMapItem, title: string, ov: ItemOverride | undefined, parents: ParentIds): Promise<void> {
  const F = effFields(item, ov)
  const { description, priority, area } = F
  switch (entry.type) {
    case 'dream': {
      // Dreams take no parent — they are the top of the ladder.
      await dreams.updateDream(entry.saasId, { title, description, priority, category: area })
      return
    }
    case 'goal': {
      await goals.updateGoal(entry.saasId, {
        title, description, priority, category: area,
        // The API types the field as number|undefined; null (detach — the
        // goal moved to the top level) passes through the same PUT.
        parent_dream_id: (parents.parent_dream_id ?? null) as unknown as number,
      })
      return
    }
    case 'skill': {
      await skills.updateSkill(String(entry.saasId), {
        title, description, priority, life_area: area,
        parent_goal_id: parents.parent_goal_id ?? null,
        parent_dream_id: parents.parent_dream_id ?? null,
      })
      return
    }
    case 'habit': {
      // UpdateHabitRequest carries no parent fields; send them anyway via a
      // non-literal payload — the backend takes what it knows.
      const payload = {
        title, description, priority, life_area: area,
        target_cadence: F.cadence,
        preferred_days: F.days,
        preferred_time_window: F.window,
        preferred_start_time: F.at,
        duration_minutes: F.duration,
        parent_goal_id: parents.parent_goal_id ?? null,
        parent_dream_id: parents.parent_dream_id ?? null,
      }
      await habits.updateHabit(String(entry.saasId), payload)
      return
    }
    case 'task': {
      await tasks.updateTask(entry.saasId, {
        title, description, priority, category: area,
        due_date: F.due,
        due_time: F.at ?? null,
        // A refinement said in a later turn adds that turn: keep the copy's
        // provenance current so a reminder from it can still bind.
        lifecoach_turn_ids: item.turn_ids?.length ? item.turn_ids : undefined,
        parent_goal_id: parents.parent_goal_id ?? null,
        parent_dream_id: parents.parent_dream_id ?? null,
      })
      return
    }
  }
}

async function deleteOne(entry: LedgerEntry): Promise<void> {
  switch (entry.type) {
    case 'dream': return dreams.deleteDream(entry.saasId).then(() => undefined)
    case 'goal': return goals.deleteGoal(entry.saasId).then(() => undefined)
    case 'skill': return skills.deleteSkill(String(entry.saasId)).then(() => undefined)
    case 'habit': return habits.deleteHabit(String(entry.saasId)).then(() => undefined)
    case 'task': return tasks.deleteTask(entry.saasId).then(() => undefined)
  }
}

/**
 * Push the map's current view of an ALREADY-CREATED item to its page item:
 * title, fields, parent pointers — and, when the effective type changed,
 * recreate it as the new type and re-point committed children. Ancestors
 * the user placed it under are created first if they are not on the pages
 * yet. Returns every ledger entry it created or replaced.
 */
export async function syncMaterialized(item: LifeMapItem, deps: MaterializeDeps): Promise<Ledger> {
  let ledger = loadLedger(deps.uid)
  const key = String(item.id)
  const entry = ledger[key]
  if (!entry) throw new Error(`"${item.title}" is not on your pages yet`)
  const typeOf = deps.typeOf ?? materializableType
  const titleOf = deps.titleOf ?? ((i: LifeMapItem) => i.display_title || i.title)
  const newType = typeOf(item)
  if (!newType) throw new Error(`"${item.title}" has no page type to update`)

  const changed: Ledger = {}
  // The displayed parent chain must exist on the pages before pointers can
  // aim at it. materialize() skips everything already in the ledger.
  const parent = deps.parentOf(item.id)
  if (parent) {
    try { Object.assign(changed, await materialize(parent, deps)) } catch { /* self-knowledge parent — pointers skip it */ }
    ledger = loadLedger(deps.uid)
  }
  const parents = committedParentIds(item, deps, ledger)
  const ov = deps.overrideOf?.(item)
  const title = titleOf(item)

  if (newType !== entry.type) {
    await deleteOne(entry)
    const saasId = await createOne(item, newType, parents, title, ov)
    const ne: LedgerEntry = { type: newType, saasId, title, fp: fingerprintOf(item) }
    ledger[key] = ne
    changed[key] = ne
    saveLedger(deps.uid, ledger)
    // Committed children still point at the deleted page item — re-point.
    for (const child of deps.childrenOf?.(item.id) ?? []) {
      const ce = ledger[String(child.id)]
      if (!ce) continue
      const cp = committedParentIds(child, deps, ledger)
      try { await updateOne(ce, child, titleOf(child), deps.overrideOf?.(child), cp) } catch { /* best effort */ }
    }
  } else {
    await updateOne(entry, item, title, ov, parents)
    const ne: LedgerEntry = { ...entry, title, fp: fingerprintOf(item) }
    ledger[key] = ne
    changed[key] = ne
    saveLedger(deps.uid, ledger)
  }
  return changed
}

/**
 * Take an item OFF the pages: delete its page item and forget the ledger
 * entry, so the map row returns to being a proposal. Children created from
 * the map keep their own page items (the backend decides orphan handling).
 */
export async function removeMaterializedByKey(uid: string, key: string): Promise<void> {
  const ledger = loadLedger(uid)
  const entry = ledger[key]
  if (!entry) return
  await deleteOne(entry)
  delete ledger[key]
  saveLedger(uid, ledger)
}

export async function removeMaterialized(item: LifeMapItem, uid: string): Promise<void> {
  return removeMaterializedByKey(uid, String(item.id))
}

// ── Version history: undo / redo / roll to any state ──────────────────────
//
// Every user action on the proposal pushes a SNAPSHOT of the map's editing
// state: the overrides, the custom drafts, and WHICH items were on the pages
// (by engine id — never by saas id, because a delete/recreate cycle mints a
// new one). Rolling to a snapshot = restore overrides+custom, then reconcile
// the pages: delete what the snapshot didn't have, recreate what it did,
// re-sync what changed. The panel orchestrates that with the primitives
// above; this is just the store.

export interface HistoryEntry {
  label: string
  at: string
  overrides: Overrides
  custom: CustomDraft[]
  /** Ledger keys (engine/custom ids as strings) committed at that moment. */
  committed: string[]
}

export interface MapHistory {
  states: HistoryEntry[]
  cursor: number
}

export const HISTORY_MAX = 50

const historyKey = (uid: string) => `${BRAND.assistant}-lifemap-history:${uid}`

export function loadHistory(uid: string): MapHistory | null {
  try {
    const raw = localStorage.getItem(historyKey(uid))
    if (!raw) return null
    const h = JSON.parse(raw) as MapHistory
    if (!h || !Array.isArray(h.states) || typeof h.cursor !== 'number') return null
    if (h.states.length === 0 || h.cursor < 0 || h.cursor >= h.states.length) return null
    return h
  } catch { return null }
}

export function saveHistory(uid: string, h: MapHistory) {
  try { localStorage.setItem(historyKey(uid), JSON.stringify(h)) } catch { /* full */ }
}
